import React from "react";
import { Link } from "react-router-dom";
import { TopBar } from "@/components/Nav";
import { Shield, Flame, Target, ChevronRight, CheckCircle2 } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1605296867304-46d5465a13f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxtdXNjdWxhciUyMG1hbiUyMGRhcmslMjBneW18ZW58MHx8fHwxNzkxNTI1NDYzfDA&ixlib=rb-4.1.0&q=85";
const FOOD_IMG = "https://images.unsplash.com/photo-1543352632-5a4b24e4d2a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxoZWFsdGh5JTIwbWVhbCUyMHByZXB8ZW58MHx8fHwxNzkxNTI1NDYzfDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <TopBar />
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-6xl mx-auto px-5 py-24 md:py-36">
          <div className="text-gold text-xs uppercase tracking-[0.3em] mb-4" data-testid="hero-eyebrow">Kaihau Warrior Coaching</div>
          <h1 className="font-display font-black uppercase text-5xl sm:text-6xl md:text-7xl leading-[0.9] tracking-tighter max-w-3xl">
            Build Discipline. <br/>Transform Your Body. <br/><span className="text-gold">Lead by Example.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-neutral-300 max-w-xl leading-relaxed">
            A 12-week transformation programme built for Pacific Island men aged 30–50 who are ready to lose fat, build muscle, and lead their families with strength.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/apply" className="kw-btn-primary" data-testid="hero-cta-apply">Apply for Coaching</Link>
            <Link to="/program" className="kw-btn-outline" data-testid="hero-cta-program">The Programme <ChevronRight className="inline w-4 h-4"/></Link>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-6xl mx-auto px-5 py-16 md:py-24 grid md:grid-cols-3 gap-4">
        {[
          {icon: Shield, title: "Discipline First", body: "Daily check-ins, non-negotiable habits, and weekly reviews that keep you accountable."},
          {icon: Flame, title: "Pacific Nutrition", body: "Macro-tracked meals built around taro, kumala, fish, lamb — foods your family already eats."},
          {icon: Target, title: "Clear Targets", body: "Personalised calorie and protein targets. No crash diets. Sustainable fat loss and muscle gain."},
        ].map((p, i) => (
          <div key={i} className="kw-card p-6" data-testid={`pillar-${i}`}>
            <p.icon className="w-7 h-7 text-gold" />
            <h3 className="font-display font-bold uppercase text-2xl mt-4 tracking-tight">{p.title}</h3>
            <p className="text-neutral-400 mt-2 leading-relaxed">{p.body}</p>
          </div>
        ))}
      </section>

      {/* 12-week visual */}
      <section className="bg-[#0f0f0f] border-y border-[#1f1f1f] py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-5">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-gold text-xs uppercase tracking-[0.3em]">The journey</div>
              <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tighter">12-Week Warrior Transformation</h2>
            </div>
            <Link to="/program" className="kw-btn-outline" data-testid="program-link">See each week</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="kw-card p-4" data-testid={`week-card-${i+1}`}>
                <div className="text-gold font-display font-black text-3xl leading-none">W{i+1}</div>
                <div className="text-xs uppercase tracking-widest text-neutral-400 mt-2">Week {i+1}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nutrition */}
      <section className="max-w-6xl mx-auto px-5 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="text-gold text-xs uppercase tracking-[0.3em]">Nutrition</div>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tighter">Fuel like a warrior,<br/>eat like family.</h2>
          <ul className="mt-6 space-y-3 text-neutral-300">
            {["Mifflin-St Jeor calorie targets (estimates, not guarantees)", "Pacific Island food database: taro, cassava, kumala, oka, palusami", "Add your own family meals and portions", "Daily macro tracker tuned to your goal"].map((t, i) => (
              <li key={i} className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-gold mt-0.5"/>{t}</li>
            ))}
          </ul>
        </div>
        <div className="relative aspect-[4/3] overflow-hidden border border-[#1f1f1f]">
          <img src={FOOD_IMG} alt="Pacific meal" className="w-full h-full object-cover"/>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0f0f0f] border-t border-[#1f1f1f] py-16">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tighter">Ready to lead by example?</h2>
          <p className="text-neutral-400 mt-3">Free consultation. No pressure. Honest answers about what the programme can and can't do.</p>
          <div className="mt-6 flex justify-center gap-3 flex-wrap">
            <Link to="/apply" className="kw-btn-primary" data-testid="footer-cta-apply">Request Free Consultation</Link>
            <Link to="/pricing" className="kw-btn-outline" data-testid="footer-cta-pricing">See Pricing</Link>
          </div>
        </div>
      </section>

      <footer className="py-10 border-t border-[#1f1f1f]">
        <div className="max-w-6xl mx-auto px-5 flex flex-wrap justify-between text-xs uppercase tracking-widest text-neutral-500 gap-4">
          <div>© 2026 Kaihau Warrior Coaching · Aotearoa New Zealand</div>
          <div className="flex gap-4">
            <Link to="/privacy" data-testid="footer-privacy">Privacy</Link>
            <Link to="/login" data-testid="footer-login">Client Login</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
